import type {Icon} from '@primer/octicons-react'
import {KebabHorizontalIcon, SortAscIcon, SortDescIcon} from '@primer/octicons-react'
import {ListFilterButton} from '@github-ui/list-filter-button'
import {ActionBarButtonAnchor} from '@github-ui/list-view/ActionBarButtonAnchor'
import {useMediaQuery} from '@github-ui/use-media-query'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {type ComponentProps, useState} from 'react'

// Matches Primer's narrow viewport range (`--viewportRange-narrow`).
const NARROW_VIEWPORT_MEDIA_QUERY = '(max-width: calc(48rem - 0.02px))'

export interface OrderOptions {
  label: string
  defaultDirection?: SortingDirection
  buttonLabel?: string | Record<SortingDirection, string>
  directionLabels?: Record<SortingDirection, string>
  icon?: Icon
  subOptions?: Record<string, {label: string; emoji?: string}>
}

export interface SortingDropdownProps {
  sortingItemSelected: string
  onSortingItemSelect: (key: string) => void
  defaultSortingKey: string
  sortingOptions: Record<string, OrderOptions>
  directionRestrictedKeys?: string[]
  sortFieldsRequireDescSuffix?: boolean
  /** Legacy ActionBar passes `false` inline and `true` in overflow. Omit for standalone layout. */
  nested?: boolean
  /** Register the trigger with Primer ActionBar and preserve popup behavior in overflow. */
  isActionBarItem?: boolean
}

const ASCENDING_SUFFIX = '-asc'
const DESCENDING_SUFFIX = '-desc'
type SortingDirection = 'asc' | 'desc'

function ActionBarListFilterButton(props: ComponentProps<typeof ListFilterButton>) {
  return <ListFilterButton {...props} isActionBarItem />
}

export function getSortingOptionLabel(sortingKey: string, sortingOptions: Record<string, OrderOptions>) {
  return sortingOptions[sortingKey]?.label || sortingKey
}

/**
 * Finds the parent option and sub-option that a resolved sorting field maps to.
 *
 * A sub-option field is composed as `${parentKey}-${subKey}` (e.g. `reactions-+1`),
 * so this walks the options that declare `subOptions` and returns the match, if any.
 */
export function findSelectedSubOption(sortingField: string, sortingOptions: Record<string, OrderOptions>) {
  for (const [parentKey, option] of Object.entries(sortingOptions)) {
    if (!option.subOptions) continue

    const prefix = `${parentKey}-`
    if (!sortingField.startsWith(prefix)) continue

    const subKey = sortingField.substring(prefix.length)
    const subOption = option.subOptions[subKey]
    if (subOption) {
      return {parentKey, subKey, label: subOption.label}
    }
  }

  return undefined
}

export function getSortingKeyAndDirection(sorting: string, defaultSortingKey: string) {
  const selectedKey = sorting || defaultSortingKey
  return splitOrderDirection(selectedKey)
}

function splitOrderDirection(value: string): [string, string] {
  if (value.endsWith(DESCENDING_SUFFIX)) {
    return [value.substring(0, value.length - DESCENDING_SUFFIX.length), 'desc']
  }

  if (value.endsWith(ASCENDING_SUFFIX)) {
    return [value.substring(0, value.length - ASCENDING_SUFFIX.length), 'asc']
  }

  return [value, '']
}

/**
 * SortingDropdown component for selecting sorting options.
 *
 * This component allows users to choose a sorting option from a dropdown menu.
 * It provides a visual indication of the current sorting state and allows users to change the sorting order.
 * defaultSortingKey should exist as a key on the sortingOptions.
 */
export function SortingDropdown({
  sortingItemSelected,
  onSortingItemSelect,
  defaultSortingKey,
  sortingOptions,
  directionRestrictedKeys,
  sortFieldsRequireDescSuffix,
  nested,
  isActionBarItem = false,
}: SortingDropdownProps) {
  const [field, direction] = getSortingKeyAndDirection(sortingItemSelected, defaultSortingKey)
  const selectedSubOption = findSelectedSubOption(field, sortingOptions)
  const isSubOptionSelected = Boolean(selectedSubOption)
  const isAscending = direction === 'asc'
  const orderIcon = isAscending ? SortAscIcon : SortDescIcon
  const canSelectDirection = !directionRestrictedKeys?.includes(field)
  const isNarrow = useMediaQuery(NARROW_VIEWPORT_MEDIA_QUERY)
  const [open, setOpen] = useState(false)

  function select(newField: string, newDirection: SortingDirection | undefined) {
    if (newDirection === 'asc') {
      onSortingItemSelect(`${newField}-asc`)
    } else if (newField === defaultSortingKey) {
      onSortingItemSelect('')
    } else if (newDirection === 'desc' && sortFieldsRequireDescSuffix) {
      onSortingItemSelect(`${newField}-desc`)
    } else {
      onSortingItemSelect(newField)
    }
  }

  const activeDirection: SortingDirection = isAscending ? 'asc' : 'desc'
  const selectedOption = sortingOptions[field]
  const configuredButtonLabel = selectedOption?.buttonLabel
  const buttonLabel = selectedSubOption
    ? selectedSubOption.label
    : typeof configuredButtonLabel === 'string'
      ? configuredButtonLabel
      : configuredButtonLabel?.[activeDirection] || getSortingOptionLabel(field, sortingOptions)
  const buttonAriaLabel = `Sort by ${buttonLabel} ${isAscending ? 'ascending' : 'descending'}`

  const regularAnchor = (
    <ActionMenu.Anchor>
      {isActionBarItem ? (
        <ActionBarButtonAnchor as={ActionBarListFilterButton} leadingVisual={orderIcon} aria-label={buttonAriaLabel}>
          {/* Primer derives the generated overflow item label from the button's children. */}
          <span className="sr-only">Sort by </span>
          {buttonLabel}
          <span className="sr-only"> {isAscending ? 'ascending' : 'descending'}</span>
        </ActionBarButtonAnchor>
      ) : (
        <ListFilterButton
          leadingVisual={orderIcon}
          aria-label={buttonAriaLabel}
          aria-expanded={open}
          aria-haspopup="menu"
        >
          {buttonLabel}
        </ListFilterButton>
      )}
    </ActionMenu.Anchor>
  )

  // Parent ActionBars already decide when this control moves into overflow.
  const actionBarOwnsResponsiveLayout = nested !== undefined || isActionBarItem
  const anchor =
    isNarrow && !actionBarOwnsResponsiveLayout ? (
      <ActionMenu.Anchor>
        <IconButton icon={KebabHorizontalIcon} variant="invisible" aria-label={buttonAriaLabel} />
      </ActionMenu.Anchor>
    ) : (
      regularAnchor
    )

  const menuContents = (
    <>
      <ActionList.Group selectionVariant="single">
        <ActionList.GroupHeading>Sort by</ActionList.GroupHeading>
        {Object.entries(sortingOptions).map(([key, {label, defaultDirection, icon: FieldIcon, subOptions}]) => {
          if (subOptions) {
            const isParentSelected = field === key || field.startsWith(`${key}-`)
            return (
              <ActionMenu key={key}>
                <ActionMenu.Anchor>
                  <ActionList.Item selected={isParentSelected}>
                    {FieldIcon && (
                      <ActionList.LeadingVisual>
                        <FieldIcon />
                      </ActionList.LeadingVisual>
                    )}
                    {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
                    {label}
                  </ActionList.Item>
                </ActionMenu.Anchor>
                <ActionMenu.Overlay>
                  <ActionList selectionVariant="single">
                    {Object.entries(subOptions).map(([subKey, {label: subLabel, emoji}]) => {
                      const subField = `${key}-${subKey}`
                      return (
                        <ActionList.Item
                          key={subKey}
                          selected={field === subField}
                          onSelect={() => select(subField, defaultDirection)}
                        >
                          {emoji && <ActionList.LeadingVisual aria-hidden="true">{emoji}</ActionList.LeadingVisual>}
                          {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
                          {subLabel}
                        </ActionList.Item>
                      )
                    })}
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            )
          }

          return (
            <ActionList.Item key={key} selected={key === field} onSelect={() => select(key, defaultDirection)}>
              {FieldIcon && (
                <ActionList.LeadingVisual>
                  <FieldIcon />
                </ActionList.LeadingVisual>
              )}
              {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
              {label}
            </ActionList.Item>
          )
        })}
      </ActionList.Group>
      {canSelectDirection && (
        <>
          <ActionList.Divider />
          <ActionList.Group selectionVariant="single">
            <ActionList.GroupHeading>Order</ActionList.GroupHeading>
            <ActionList.Item
              key="ascending"
              disabled={isSubOptionSelected}
              selected={isAscending}
              onSelect={() => select(field, 'asc')}
            >
              <ActionList.LeadingVisual>
                <SortAscIcon />
              </ActionList.LeadingVisual>
              <span>{selectedOption?.directionLabels?.asc || 'Ascending'}</span>
            </ActionList.Item>
            <ActionList.Item
              key="descending"
              disabled={isSubOptionSelected}
              selected={!isAscending}
              onSelect={() => select(field, 'desc')}
            >
              <ActionList.LeadingVisual>
                <SortDescIcon />
              </ActionList.LeadingVisual>
              <span>{selectedOption?.directionLabels?.desc || 'Descending'}</span>
            </ActionList.Item>
          </ActionList.Group>
        </>
      )}
    </>
  )

  if (nested) {
    return menuContents
  }

  return (
    <ActionMenu open={open} onOpenChange={setOpen}>
      {anchor}
      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">{menuContents}</ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
